import React from 'react';
import { 
  Facebook, 
  Twitter, 
  Linkedin, 
  MessageCircle, 
  Send,
  Share2
} from 'lucide-react';

const SocialShareButtons = ({ url, title, description, image }) => {
  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(title + ' - ' + url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
  };

  const handleShare = (platform) => {
    window.open(shareLinks[platform], '_blank', 'width=600,height=400');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: url,
        });
      } catch (err) {
        console.log('Share failed:', err);
      }
    }
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => handleShare('whatsapp')}
        className="w-10 h-10 rounded-full bg-green-600 hover:bg-green-700 flex items-center justify-center transition-colors"
        title="Share on WhatsApp"
      >
        <MessageCircle className="h-5 w-5 text-white" />
      </button>
      
      <button
        onClick={() => handleShare('facebook')}
        className="w-10 h-10 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center transition-colors"
        title="Share on Facebook"
      >
        <Facebook className="h-5 w-5 text-white" fill="white" />
      </button>
      
      <button
        onClick={() => handleShare('twitter')}
        className="w-10 h-10 rounded-full bg-sky-500 hover:bg-sky-600 flex items-center justify-center transition-colors"
        title="Share on Twitter"
      >
        <Twitter className="h-5 w-5 text-white" fill="white" />
      </button>
      
      <button
        onClick={() => handleShare('linkedin')}
        className="w-10 h-10 rounded-full bg-blue-700 hover:bg-blue-800 flex items-center justify-center transition-colors"
        title="Share on LinkedIn"
      >
        <Linkedin className="h-5 w-5 text-white" fill="white" />
      </button>
      
      <button
        onClick={() => handleShare('telegram')}
        className="w-10 h-10 rounded-full bg-sky-600 hover:bg-sky-700 flex items-center justify-center transition-colors"
        title="Share on Telegram"
      >
        <Send className="h-5 w-5 text-white" />
      </button>

      {/* Instagram - Deep link */}
      <button
        onClick={() => {
          const instagramUrl = `instagram://library?AssetPath=${encodeURIComponent(image || url)}`;
          window.location.href = instagramUrl;
          setTimeout(() => {
            window.open('https://www.instagram.com/', '_blank');
          }, 500);
        }}
        className="w-10 h-10 rounded-full bg-gradient-to-tr from-purple-600 via-pink-600 to-orange-500 hover:opacity-90 flex items-center justify-center transition-opacity"
        title="Share on Instagram"
      >
        <svg className="h-5 w-5 text-white" fill="white" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      </button>

      {/* TikTok */}
      <button
        onClick={() => window.open('https://www.tiktok.com/upload', '_blank')}
        className="w-10 h-10 rounded-full bg-black hover:bg-gray-800 flex items-center justify-center transition-colors"
        title="Share on TikTok"
      >
        <svg className="h-5 w-5 text-white" fill="white" viewBox="0 0 24 24">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        </svg>
      </button>

      {/* Snapchat */}
      <button
        onClick={() => window.open('https://www.snapchat.com/scan', '_blank')}
        className="w-10 h-10 rounded-full bg-yellow-400 hover:bg-yellow-500 flex items-center justify-center transition-colors"
        title="Share on Snapchat"
      >
        <svg className="h-5 w-5 text-black" fill="black" viewBox="0 0 24 24">
          <path d="M12.166 3c-2.763 0-5.027 2.264-5.027 5.027 0 1.443.614 2.755 1.597 3.68-.485.878-1.092 1.558-1.866 2.075-.126.084-.209.214-.209.36 0 .235.19.425.425.425.043 0 .085-.006.126-.02.927-.301 1.56-.468 2.264-.468.332 0 .628.046.962.138-.112.246-.169.507-.169.778 0 1.024.837 1.861 1.861 1.861.532 0 1.012-.225 1.352-.584.34.359.82.584 1.352.584 1.024 0 1.861-.837 1.861-1.861 0-.271-.057-.532-.169-.778.334-.092.63-.138.962-.138.704 0 1.337.167 2.264.468.041.014.083.02.126.02.235 0 .425-.19.425-.425 0-.146-.083-.276-.209-.36-.774-.517-1.381-1.197-1.866-2.075.983-.925 1.597-2.237 1.597-3.68 0-2.763-2.264-5.027-5.027-5.027zm0 1.197c2.12 0 3.83 1.71 3.83 3.83 0 1.334-.686 2.516-1.723 3.208-.103.069-.166.188-.166.315 0 .055.014.108.041.156.468.838 1.054 1.545 1.783 2.147-.674-.163-1.242-.243-1.747-.243-.442 0-.838.062-1.277.194-.126.038-.245.095-.35.169-.34.24-.809.379-1.312.379s-.972-.139-1.312-.379c-.105-.074-.224-.131-.35-.169-.439-.132-.835-.194-1.277-.194-.505 0-1.073.08-1.747.243.729-.602 1.315-1.309 1.783-2.147.027-.048.041-.101.041-.156 0-.127-.063-.246-.166-.315-1.037-.692-1.723-1.874-1.723-3.208 0-2.12 1.71-3.83 3.83-3.83z"/>
        </svg>
      </button>

      {/* Native Share (for mobile) */}
      {navigator.share && (
        <button
          onClick={handleNativeShare}
          className="w-10 h-10 rounded-full bg-gray-600 hover:bg-gray-700 flex items-center justify-center transition-colors"
          title="More sharing options"
        >
          <Share2 className="h-5 w-5 text-white" />
        </button>
      )}
    </div>
  );
};

export default SocialShareButtons;
